"use client";

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import createSvgCaptcha from "@/utils/createSvgCaptcha";
import { FormHelperText, OutlinedInput } from "@mui/material";
import { RxReload } from "react-icons/rx";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import cn from "@/utils/cn";
import Button from "@/components/Button";

const Captcha = forwardRef(function ({ disabled = false }, ref) {
  const [captcha, setCaptcha] = useState(null);
  const timeoutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const createSvg = () => {
    setIsLoading(true);
    setInputValue("");
    createSvgCaptcha()
      .then(setCaptcha)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setMounted(true);
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(createSvg, 0);

    return () => clearTimeout(timeoutRef.current);
  }, []);

  useImperativeHandle(ref, () => ({
    getResponse: () => {
      return !isLoading && inputValue === captcha?.text;
    },
    reset: () => {
      setInputValue("");
      createSvgCaptcha().then(setCaptcha);
    },
  }));

  const isValid =
    inputValue?.length < 4 || !captcha || inputValue === captcha.text;

  return (
    <div className="flex gap-2 flex-1 w-full items-start">
      <FormControl variant="outlined" className="w-full" error={!isValid}>
        <InputLabel htmlFor="security-code">Security code</InputLabel>
        <OutlinedInput
          label="Security code"
          id="security-code"
          className="bg-white"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        {!isValid && (
          <FormHelperText className="text-red-600 font-bold text-xs max-w-full !pr-0 !mr-0">
            Code is incorrect
          </FormHelperText>
        )}
      </FormControl>
      <div
        className={cn(
          "w-[9rem] h-[3.5rem] rounded bg-black/5 [&_svg]:max-w-full px-1",
          {
            "bg-black/20 animate-pulse": !mounted,
          },
        )}
      >
        {captcha?.data && (
          <div dangerouslySetInnerHTML={{ __html: captcha.data }} />
        )}
      </div>
      <Button
        className="min-h-[3.5rem] !bg-black/5 hover:!bg-black/10"
        onClick={createSvg}
        type="button"
        disabled={disabled || isLoading}
      >
        <span className="sr-only">regenerate captcha</span>
        <RxReload
          className={cn("text-base text-black", {
            "animate-spin": isLoading,
          })}
        />
      </Button>
    </div>
  );
});
Captcha.displayName = "Captcha";

export default Captcha;
